import type { Router } from 'vue-router'

import { useDeployVersionStore } from '@/stores/deployVersion'

export const DEPLOY_VERSION_PATH = '/version.json'

export const DEFAULT_POLL_MS = 10 * 60 * 1000

export interface DeployVersionManifest {
  version: string
  commit: string
  buildHash: string
  builtAt: string
}

export async function fetchDeployVersionManifest(): Promise<DeployVersionManifest | null> {
  try {
    const response = await fetch(`${DEPLOY_VERSION_PATH}?t=${Date.now()}`, {
      cache: 'no-store',
      headers: { Accept: 'application/json' }
    })
    if (!response.ok) return null
    const json = (await response.json()) as Partial<DeployVersionManifest>
    if (!json.buildHash || typeof json.buildHash !== 'string') return null
    return {
      version: json.version ?? 'unknown',
      commit: json.commit ?? 'unknown',
      buildHash: json.buildHash,
      builtAt: json.builtAt ?? ''
    }
  } catch {
    return null
  }
}

function resolvePollIntervalMs(): number {
  const raw = import.meta.env.VITE_DEPLOY_VERSION_POLL_MS
  if (raw === undefined || raw === '') return DEFAULT_POLL_MS
  const parsed = Number(raw)
  if (!Number.isFinite(parsed) || parsed < 0) return DEFAULT_POLL_MS
  return parsed
}

export function startDeployVersionPolling(router: Router): void {
  if (import.meta.env.DEV) return

  const store = useDeployVersionStore()
  const intervalMs = resolvePollIntervalMs()
  let intervalId: ReturnType<typeof setInterval> | null = null

  if (intervalMs > 0) {
    intervalId = setInterval(() => {
      if (store.deployStale && intervalId !== null) {
        clearInterval(intervalId)
        intervalId = null
        return
      }
      void store.pollDeployVersion()
    }, intervalMs)
  }

  document.addEventListener('visibilitychange', () => {
    if (document.visibilityState === 'visible') {
      void store.pollDeployVersion()
    }
  })

  router.afterEach(() => {
    void store.pollDeployVersion()
  })
}
